import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys, QueryKeys } from '../../api/config';
import { useLoadsInTransit } from '../../api/queries/use-loads-in-transit';
import { useOrganizationQuery } from '../../api/queries/use-organization-query';
import { useStaticQuery } from '../../api/queries/use-static-query';
import '../../scss/here-map.scss';
import '../../scss/live-map.scss';
import { HereMapColors } from '../../styles/agistics-colors';
import type { AvoidanceZone, DeviceLocation, Driver, Organization, OrganizationLocation } from '../../types';
import { toSiteDisplayName } from '../../utils/data-mapping';
import { AvoidanceZoneBox } from '../here-maps/avoidance-zone-box';
import { HereMap, HereMapRefObject } from '../here-maps/here-map';
import { HereMapHeading } from '../here-maps/here-map-heading';
import { HereMapMarker } from '../here-maps/here-map-marker';
import { HereMapRectangle } from '../here-maps/here-map-rectangle';
import { HereMapRoute } from '../here-maps/here-map-route';
import { ContentHeader } from '../shared/containers/content-header';
import { useUpdateUserPreferences } from '../shared/hooks/use-update-user-preferences';
import { DriverFilter } from './components/driver-filter';
import { SiteLocator } from './components/site-locator';
import { DriverRouteRecalculation } from './driver-route-recalculation';
import { LiveMapList } from './live-map-list';
import { LiveMapLoadDetails } from './live-map-load-details';
import { DeviceLocationMarker } from './markers/device-location-marker';
import { getStopIcon } from './markers/live-map-stop-icon';
import { useConnectedDeviceLocations } from './utils/use-connected-device-locations';
import { usePastLoadLocations } from './utils/use-past-load-locations';

interface LiveMapRectangleProps {
    organizationLocation: OrganizationLocation;
}

const LiveMapRectangle = (props: LiveMapRectangleProps) => {
    const { organizationLocation } = props;
    const { id, name, description, customGeofence } = organizationLocation;
    const locationColor = organizationLocation.orgLocationType?.locationTypeColor?.hexColor;

    return (
        <HereMapRectangle
            key={id}
            pointA={{ lat: customGeofence.bbox.topLeft.latitude, lng: customGeofence.bbox.topLeft.longitude }}
            pointB={{ lat: customGeofence.bbox.bottomRight.latitude, lng: customGeofence.bbox.bottomRight.longitude }}
            strokeColor={locationColor || HereMapColors.LOCATION_DEFAULT_STROKE}
            type='avoidance' //this should probably be renamed, type decides if when you hit the recenter map button if the rectangle should be included in the recenter math
            changeable={false}
        >
            <div>
                <span className='map-tooltip-label-name'>{name}</span>
                {description && <span className='map-tooltip-label-desc'>{description}</span>}
            </div>
        </HereMapRectangle>
    );
}

const LiveMapUtilsContainer = styled('div')({
    display: 'flex',
    alignItems: 'flex-end',
    '& > :not(:last-child)': {
        marginRight: '16px'
    }
});

// boxes / markers rendered inside HERE map needs to be memoized

// As deviceLocations are received from the websocket, this whole component gets re-rendered it would require quite of bit of refactoring to fix / change

// there is a memory leak related to adding/removing items to the map
// we think somewhere in HereMapRectangle, but really not sure
// we did confirm that it might be that we are not removing event listeners (pointerenter / pointerleave), but did not seam to be the only problem
// it might also be some issue with the HERE sdk with calling map.removeObject not tearing down the object completely (un-tested)

// can recreate (by not waiting hours to see leak) by setting up code to call /devices/locations every 50ms
// see fake-pings in api project
export const LiveMap = () => {
    const mapRef = React.useRef<HereMapRefObject>(null);
    const { data: avoidanceZones = [] } = useStaticQuery<AvoidanceZone[]>(QueryKeys.avoidanceZones);
    const { data: organization, isLoading } = useOrganizationQuery<Organization>('');
    const { data: organizationLocations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { data: drivers = [] } = useOrganizationQuery<Driver[]>(OrgQueryKeys.drivers);
    const { data: loadsInTransit, isLoading: isLoadsLoading, refetch: refetchLoadsInTransit } = useLoadsInTransit();
    const { latestDeviceLocations } = useConnectedDeviceLocations();
    const { preferences } = useUpdateUserPreferences();
    
    const [selectedDeviceLocationId, setSelectedDeviceLocationId] = React.useState<number | null>(null);
    const [selectedOrgLocation, setSelectedOrgLocation] = React.useState<OrganizationLocation | null>(null);
    const [removedDrivers, setRemovedDrivers] = React.useState<number[]>(preferences?.removedDrivers || []);
    
    const selectedDeviceLocation = latestDeviceLocations.find(x => x.deviceId === selectedDeviceLocationId);
    const pastLoadLocations = usePastLoadLocations(selectedDeviceLocation);
    const activeLoad = loadsInTransit?.find(x => x.id === selectedDeviceLocation?.orderId);

    const mappedRouteDeviceLocations = React.useMemo(() => {
        return pastLoadLocations.filter(x => x.accuracy && x.accuracy < 100).map(location => [location.latitude, location.longitude, null]);
    }, [pastLoadLocations]);

    const filteredDeviceLocations = React.useMemo(() => {
        return latestDeviceLocations.filter(x => !removedDrivers.includes(x.userId));
    }, [latestDeviceLocations, removedDrivers]);

    const stopLocations = activeLoad?.stops
            .sortBy(stop => stop.sequence)
            .map(stop => {
                const orgLoc = organizationLocations?.find(l => l.id === stop.organizationLocationId);
                const organizationSublocations = orgLoc?.orgSubLocations;
                const customGeofence = orgLoc?.customGeofence;
                const tooltip = orgLoc ? toSiteDisplayName(orgLoc.name, orgLoc.description) : '';
    
                return {
                    id: stop.id,
                    organizationLocationId: orgLoc?.id || -1,
                    customGeofence,
                    sequence: stop.sequence,
                    tooltip: tooltip,
                    organizationSublocations: organizationSublocations,
                };
            })
            .filter(stop => stop.customGeofence?.latitude && stop.customGeofence?.longitude);

    const waypoints = stopLocations?.map(stopLocation => ({
        ...stopLocation,
        latitude: stopLocation.customGeofence?.latitude,
        longitude: stopLocation.customGeofence?.longitude,
    }));

    const markers = waypoints?.map((waypoint, i) => (
        <HereMapMarker key={i}
            icon={getStopIcon(waypoint.sequence)}
            latitude={waypoint.latitude}
            longitude={waypoint.longitude}/>
    ));

    React.useEffect(() => {
        if (selectedOrgLocation && selectedOrgLocation.customGeofence) {
            mapRef.current?.centerMap(selectedOrgLocation.customGeofence.latitude, selectedOrgLocation.customGeofence.longitude, 14);
        }
    }, [selectedOrgLocation]);

    const toggleSelected = (deviceLocation: DeviceLocation) => {
        if (deviceLocation.deviceId === selectedDeviceLocationId) {
            setSelectedDeviceLocationId(null);
        } else {
            // this refetch ensures up to date load info when selecting a device, since loadsInTransit is only stale after 1 min
            // important so `activeLoad` can correctly find the load for the selected device
            refetchLoadsInTransit();
            mapRef.current?.centerMap(deviceLocation.latitude, deviceLocation.longitude, 14);
            setSelectedDeviceLocationId(deviceLocation.deviceId);
        }
    }

    const toggleSelectedOrgLocation = React.useMemo(() => {
        return (orgLoc: OrganizationLocation | null) => {
            setSelectedOrgLocation(orgLoc);
        };
    }, []);

    const filteredAvoidanceZones = avoidanceZones.filter(x => !x.deactivatedAt);

    const avoidanceZoneBoxes = React.useMemo(() => {
        return filteredAvoidanceZones.map((avoidanceZone, i) =>
            <AvoidanceZoneBox key={i} avoidanceZone={avoidanceZone} />)
    }, [avoidanceZones]); // avoidanceZones NOT filteredAvoidanceZones (see above for more info on useMemo)

    const orgLocations = React.useMemo(() => {
        return organizationLocations
                    .filter(x => x.enabled)
                    .map((orgLocation) =>
                        orgLocation.customGeofence && orgLocation.customGeofence.bbox &&
                            <LiveMapRectangle
                                key={orgLocation.id}
                                organizationLocation={orgLocation}
                            />
        );
    }, [organizationLocations]);

    return (
        <div className={'live-map-container'}>
            <ContentHeader>
                <Typography variant='h5'>{L.liveMap()}</Typography>
                <LiveMapUtilsContainer>
                    <DriverFilter
                        drivers={drivers}
                        removedDrivers={removedDrivers}
                        setRemovedDrivers={setRemovedDrivers} />
                    <SiteLocator
                        sites={organizationLocations}
                        onChange={toggleSelectedOrgLocation} />
                </LiveMapUtilsContainer>
            </ContentHeader>
            <div className={'live-map-content'}>
                <div className='live-map-sidebar'>
                    {selectedDeviceLocation?.deviceId && activeLoad
                        ?
                        <LiveMapLoadDetails
                            deviceLocation={selectedDeviceLocation}
                            load={activeLoad}
                            isLoading={isLoadsLoading}
                            onClose={() => setSelectedDeviceLocationId(null)} />
                        :
                        <LiveMapList deviceLocations={filteredDeviceLocations} onClick={(x) => toggleSelected(x)} selectedDevice={selectedDeviceLocation} />
                    }
                </div>
                <div className='here-map-container'>
                    {!isLoading && organization && <HereMap ref={mapRef}
                        controlOptions={selectedDeviceLocation ? { showMobileHeading: true } : undefined}
                        defaultZoom={8}
                        centerPosition={{ lat: organization.latitude, lng: organization.longitude }}
                    >
                        {avoidanceZoneBoxes}
                        {orgLocations}
                        {markers}
                        {waypoints && waypoints.length > 1 &&
                            <HereMapRoute
                                calculateRouteFromWaypoints={true}
                                waypoints={waypoints}
                                avoidanceZones={filteredAvoidanceZones}
                            />
                        }
                        {filteredDeviceLocations?.map(deviceLocation => {
                            const driver = drivers?.find(x => x.id === deviceLocation.userId);
                            return (
                                <DeviceLocationMarker
                                    key={deviceLocation.deviceId}
                                    deviceLocation={deviceLocation}
                                    driver={driver}
                                    onClick={() => toggleSelected(deviceLocation)}
                                />
                            )}
                        )}
                        {mappedRouteDeviceLocations.length > 1 &&
                            <HereMapRoute calculateRouteFromWaypoints={false}
                                waypoints={mappedRouteDeviceLocations} />
                        }
                        {pastLoadLocations.map((location, i) => {
                            return <HereMapHeading
                                key={i}
                                location={location}
                                type={'mobile'} />;
                        })}
                        {activeLoad &&
                            <DriverRouteRecalculation
                                load={activeLoad}
                                deviceLocation={selectedDeviceLocation!}
                                avoidanceZones={filteredAvoidanceZones}
                            />
                        }
                    </HereMap>}
                </div>
            </div>
        </div>
    );
};
